import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { AlertCircle, TrendingDown } from 'lucide-react';
import { useI18n } from '@/lib/i18n';

export const Problem = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const problems = [
    {
      icon: AlertCircle,
      title: t('problem.paperBlindness'),
      description: t('problem.paperBlindnessDesc'),
    },
    {
      icon: TrendingDown,
      title: t('problem.invisibleWaste'),
      description: t('problem.invisibleWasteDesc'),
    },
  ];

  return (
    <section
      ref={ref}
      className="py-24 lg:py-32 bg-white"
      data-testid="problem-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="lg:col-span-7"
          >
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black text-primary tracking-tight leading-tight"
              data-testid="problem-title"
            >
              {t('problem.title')}
            </h2>
            <p
              className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed"
              data-testid="problem-subtitle"
            >
              {t('problem.subtitle')}
            </p>
          </motion.div>

          {/* Problem Cards */}
          <div className="lg:col-span-5 space-y-5">
            {problems.map((problem, index) => (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, x: 30 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
                className="flex items-start gap-4 p-6 bg-surface rounded-xl border border-primary/5 hover:shadow-soft transition-shadow duration-300"
                data-testid={`problem-card-${index}`}
              >
                <div className="flex-shrink-0 w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                  <problem.icon className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-primary tracking-tight">
                    {problem.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {problem.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
